import { DEFAULT_LOCALE, LOCALE_CONFIG, LOCALES, type Locale } from "./config";
import { allAttractions, attractionFromHost } from "../attractions/registry";
import { PLATFORM_LOCALES, type AttractionConfig, type PlatformLocale } from "../attractions/types";
import { localeFromPathname } from "./routing";
import { platformLocaleOrNull } from "./platform";

export type LocalizedLanguageHeaderTarget = {
  source: string;
  language: string;
};

export function documentLanguageForLocale(locale: Locale): string {
  return LOCALE_CONFIG[locale].hrefLang;
}

const PLATFORM_DOCUMENT_LANGUAGES: Record<PlatformLocale, string> = {
  en: "en",
  es: "es",
  pt: "pt-BR",
  fr: "fr",
  de: "de",
  nl: "nl",
};

const SHARED_APPLICATION_PREFIXES = ["/app", "/login", "/api", "/alert", "/go", "/__/auth", "/_next"] as const;

function isSharedApplicationPath(pathname: string | null | undefined): boolean {
  const normalized = pathname || "/";
  return SHARED_APPLICATION_PREFIXES.some((prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`));
}

export function documentLanguageForPlatformLocale(locale: PlatformLocale): string {
  return PLATFORM_DOCUMENT_LANGUAGES[locale];
}

export function documentLanguageFromPathname(pathname: string | null | undefined): string {
  const firstSegment = (pathname ?? "").split("/").filter(Boolean)[0]?.toLowerCase();
  if (firstSegment && PLATFORM_LOCALES.includes(firstSegment as PlatformLocale)) {
    return documentLanguageForPlatformLocale(firstSegment as PlatformLocale);
  }
  return documentLanguageForLocale(localeFromPathname(pathname));
}

export function documentLanguageForAttractionPath(
  attraction: Pick<AttractionConfig, "locales" | "defaultLocale">,
  pathname: string | null | undefined,
): string {
  const firstSegment = (pathname ?? "").split("/").filter(Boolean)[0]?.toLowerCase();
  const explicitLocale = firstSegment && attraction.locales.includes(firstSegment as PlatformLocale)
    ? firstSegment as PlatformLocale
    : attraction.defaultLocale;
  return documentLanguageForPlatformLocale(explicitLocale);
}

export function documentLanguageFromPublicLocation(
  pathname: string | null | undefined,
  hostname: string | null | undefined,
  requestedLocale?: unknown,
): string {
  const attraction = attractionFromHost(hostname);
  if (isSharedApplicationPath(pathname)) {
    const requested = platformLocaleOrNull(requestedLocale);
    const locale = attraction
      ? requested && attraction.locales.includes(requested) ? requested : attraction.defaultLocale
      : requested ?? DEFAULT_LOCALE;
    return documentLanguageForPlatformLocale(locale);
  }
  return attraction
    ? documentLanguageForAttractionPath(attraction, pathname)
    : documentLanguageFromPathname(pathname);
}

export function localizedLanguageHeaderTargets(): LocalizedLanguageHeaderTarget[] {
  return LOCALES.filter((locale) => locale !== DEFAULT_LOCALE).flatMap((locale) => {
    const prefix = LOCALE_CONFIG[locale].pathPrefix;
    const language = documentLanguageForLocale(locale);

    return [
      { source: prefix, language },
      { source: `${prefix}/:path*`, language },
    ];
  });
}

export function documentLanguageBootstrapScript(): string {
  const defaultLanguage = documentLanguageForLocale(DEFAULT_LOCALE);
  const localizedPrefixes = PLATFORM_LOCALES.map((locale) => ({
    prefix: `/${locale}`,
    language: documentLanguageForPlatformLocale(locale),
  }));
  const defaultLanguageByHost = Object.fromEntries(
    allAttractions().flatMap((attraction) => attraction.publicHosts.map((host) => [
      host.toLowerCase(),
      documentLanguageForPlatformLocale(attraction.defaultLocale),
    ])),
  );
  const localesByHost = Object.fromEntries(
    allAttractions().flatMap((attraction) => attraction.publicHosts.map((host) => [
      host.toLowerCase(),
      attraction.locales,
    ])),
  );
  const languageByLocale = Object.fromEntries(
    PLATFORM_LOCALES.map((locale) => [locale, documentLanguageForPlatformLocale(locale)]),
  );

  return `(()=>{var p=location.pathname;var h=location.hostname.toLowerCase();var d=${JSON.stringify(defaultLanguageByHost)};var c=${JSON.stringify(localesByHost)};var v=${JSON.stringify(languageByLocale)};var a=${JSON.stringify(SHARED_APPLICATION_PREFIXES)};var s=false;for(var j=0;j<a.length;j++){if(p===a[j]||p.indexOf(a[j]+"/")===0){s=true;break;}}var l=s?(d[h]||${JSON.stringify(defaultLanguage)}):(d[h]||${JSON.stringify(defaultLanguage)});if(s){var q=new URLSearchParams(location.search||"").get("locale");if(v[q]&&(!c[h]||c[h].indexOf(q)!==-1))l=v[q];}var m=${JSON.stringify(localizedPrefixes)};for(var i=0;i<m.length;i++){var x=m[i];if(p===x.prefix||p.indexOf(x.prefix+"/")===0){l=x.language;break;}}if(document.documentElement.lang!==l)document.documentElement.lang=l;})();`;
}
